import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { clearUrlHash } from '@cliv-portal-auth/bootstrap';

import { initialB2CIdToken, initialUrlHadB2CIdToken } from '@/app/auth-bootstrap';
import { AuthBrandShell } from '@/components/auth/AuthBrandShell';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/use-auth';
import {
  normalizeAppReturnPath,
  POST_LOGIN_REDIRECT_STORAGE_KEY,
} from '@/lib/auth/post-login-redirect';
import { logger } from '@/lib/logger';
import { getB2CLoginUrl, resolveSupplierPortalLoginError } from '@/services/auth/auth.service';

const AUTH_LOG = '[supplier-auth]';

const loginBtnClassName =
  'mb-6 inline-flex w-[65%] max-lg:w-full cursor-pointer items-center justify-center rounded-[40px] border-0 bg-[#1745DF] px-4 py-2 text-center text-[clamp(1rem,2vw,1.35rem)] font-medium capitalize text-white shadow-[0_4px_4px_rgba(0,0,0,0.25)] transition-colors hover:bg-[#04C1A2] disabled:cursor-not-allowed disabled:opacity-60 max-[500px]:my-8 max-[500px]:py-[4vw] max-[500px]:text-[5vw]';

/** Shared across React Strict Mode remounts so B2C id_token is exchanged only once. */
let b2cIdTokenExchangePromise: Promise<void> | null = null;

/** Tests: reset module-level exchange guard. */
export function __resetB2CIdTokenExchangeForTests(): void {
  b2cIdTokenExchangePromise = null;
}

/**
 * B2C redirect login: loads sign-in URL or exchanges id_token from URL hash for an app JWT.
 */
export function Login(): ReactElement {
  const navigate = useNavigate();
  const { signIn, isAuthenticated } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [b2cFlowPaused, setB2cFlowPaused] = useState(false);
  const [hasExchangedHashToken, setHasExchangedHashToken] = useState(false);
  const [redirectInitiated, setRedirectInitiated] = useState(false);
  const exchangedIdTokenRef = useRef<string | null>(null);
  const loginUrl = useMemo(() => getB2CLoginUrl(), []);

  const isB2CCallbackFlow = initialUrlHadB2CIdToken && !hasExchangedHashToken;

  const handleTokenExchange = useCallback(
    async (idToken: string) => {
      logger.debug(AUTH_LOG + ' Login: handleTokenExchange (B2C id_token from hash)');
      setIsLoading(true);
      setError(null);
      try {
        await signIn(idToken);
        setHasExchangedHashToken(true);
      } catch (err) {
        const message = resolveSupplierPortalLoginError(err);
        logger.debug(AUTH_LOG + ' Login: sign in error', { message });
        setError(message);
        setB2cFlowPaused(true);
      } finally {
        setIsLoading(false);
      }
    },
    [signIn]
  );

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }
    const fromStorage = sessionStorage.getItem(POST_LOGIN_REDIRECT_STORAGE_KEY);
    sessionStorage.removeItem(POST_LOGIN_REDIRECT_STORAGE_KEY);
    const target = normalizeAppReturnPath(fromStorage ?? undefined);
    void navigate(target, { replace: true });
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (isAuthenticated || b2cFlowPaused || hasExchangedHashToken || !initialB2CIdToken) {
      return;
    }
    if (exchangedIdTokenRef.current === initialB2CIdToken) {
      return;
    }
    if (b2cIdTokenExchangePromise) {
      void b2cIdTokenExchangePromise;
      return;
    }
    logger.debug(AUTH_LOG + ' Login: exchanging captured B2C id_token');
    exchangedIdTokenRef.current = initialB2CIdToken;
    clearUrlHash();
    b2cIdTokenExchangePromise = handleTokenExchange(initialB2CIdToken).finally(() => {
      b2cIdTokenExchangePromise = null;
    });
    void b2cIdTokenExchangePromise;
  }, [isAuthenticated, handleTokenExchange, b2cFlowPaused, hasExchangedHashToken]);

  useEffect(() => {
    if (
      initialUrlHadB2CIdToken ||
      isAuthenticated ||
      !loginUrl ||
      error ||
      isLoading ||
      b2cFlowPaused ||
      redirectInitiated ||
      hasExchangedHashToken
    ) {
      return;
    }
    logger.debug(AUTH_LOG + ' Login: redirecting to B2C (automatic)');
    setRedirectInitiated(true);
    window.location.replace(loginUrl);
  }, [
    loginUrl,
    error,
    isLoading,
    b2cFlowPaused,
    redirectInitiated,
    hasExchangedHashToken,
    isAuthenticated,
  ]);

  const handleManualRedirectToB2C = (): void => {
    if (!loginUrl) {
      return;
    }
    logger.debug(AUTH_LOG + ' Login: redirecting to B2C (manual)');
    setError(null);
    window.location.href = loginUrl;
  };

  const handleRetrySignIn = (): void => {
    setError(null);
    setB2cFlowPaused(false);
    setHasExchangedHashToken(false);
    setRedirectInitiated(false);
    if (loginUrl) {
      window.location.replace(loginUrl);
      return;
    }
    window.location.reload();
  };

  const showPrimaryAuthButton = Boolean(loginUrl) && Boolean(error);
  const showConfigRetry = Boolean(error) && !loginUrl;
  const showBlockingWait =
    !error &&
    (isLoading ||
      (!isB2CCallbackFlow &&
        ((Boolean(loginUrl) && !redirectInitiated) || (Boolean(loginUrl) && redirectInitiated))));

  const blockingMessage = isLoading ? 'Signing in...' : 'Signing you in...';

  return (
    <AuthBrandShell page="login" heroAccessibilityTitle="Supplier portal sign-in">
      {error ? (
        <div
          role="alert"
          aria-live="assertive"
          className="mb-4 max-w-lg px-2 text-center text-sm font-medium text-red-200 drop-shadow-sm"
        >
          {error}
        </div>
      ) : null}

      {!loginUrl && !error ? (
        <div
          role="alert"
          className="mb-4 max-w-lg px-2 text-center text-sm font-medium text-red-200 drop-shadow-sm"
        >
          Login is not configured. Please contact support.
        </div>
      ) : null}

      {showBlockingWait ? (
        <div className="mb-6 flex flex-col items-center gap-3" role="status" aria-live="polite">
          <div
            className="h-16 w-16 animate-spin rounded-full border-4 border-[#FFFFFF] border-t-transparent"
            aria-hidden
          />
          <p className="text-center text-sm text-[#FFFFFF]/90">{blockingMessage}</p>
        </div>
      ) : null}

      {showPrimaryAuthButton || showConfigRetry ? (
        <div className="flex w-full max-w-md flex-col items-center gap-2">
          {showPrimaryAuthButton ? (
            <button
              type="button"
              className={loginBtnClassName}
              onClick={handleManualRedirectToB2C}
              disabled={isLoading || !loginUrl}
              aria-busy={isLoading}
              aria-label="Retry sign in with your organization"
            >
              Retry sign in
            </button>
          ) : null}
          {showConfigRetry ? (
            <Button
              type="button"
              variant="outline"
              className="w-full max-w-md cursor-pointer rounded-[40px] border-[#FFFFFF] py-6 text-[#FFFFFF] hover:bg-white/15 hover:text-white"
              onClick={handleRetrySignIn}
              disabled={isLoading}
            >
              Retry sign-in
            </Button>
          ) : null}
        </div>
      ) : null}
    </AuthBrandShell>
  );
}
